import { GLOBAL_FILTER_PARAM_NAME, PAGE_PARAM_NAME } from '@/design-system/data-table/data-table';
import { format, UTCDate } from '@/utils/dates';
import { EvidenceStatus, OAuthProvider, TransactionType } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import { EVIDENCE_API_PATH } from '../../contexts/data-context/data-provider';
import { useData } from '../../contexts/data-context/use-data';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { LoadingSection } from '../../design-system/loading/loading';
import { InvoiceEvidenceView } from '../../types/entity-types';
import { deleteSwrFetcherWithDynamicUrl } from '../../utils/delete-swr-fetcher-with-dynamic-url';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../utils/get-amount-with-currency';
import { getSwrFetcher } from '../../utils/get-swr-fetcher';
import { getSwrFetcherWithDynamicUrl } from '../../utils/get-swr-fetcher-with-dynamic-url';
import { postSwrFetcher } from '../../utils/post-swr-fetcher';
import { postSwrFetcherWithDynamicUrl } from '../../utils/post-swr-fetcher-with-dynamic-url';
import { isSupportedCurrency } from '../payments/utils/is-supported-currency';
import { MenuItemVariant } from './components/invoice-data-section/invoice-table-row-options-menu';
import { ExcludeSenderModal } from './components/invoice-data-section/modals/exclude-sender-modal';
import { MarkAsPaidModal } from './components/invoice-data-section/modals/mark-as-paid-modal';
import { MarkNotInvoiceModal } from './components/invoice-data-section/modals/mark-not-invoice-modal';
import { MarkNotPaidModal } from './components/invoice-data-section/modals/mark-not-paid-modal';
import { InvoiceDrawer } from './components/invoice-drawer/invoice-drawer';
import { InvoiceDrawerContentBodyVariant } from './components/invoice-drawer/invoice-drawer-content-body';
import { useExcludeSender } from './hooks/use-exclude-sender';
import { InvoicePayPage } from './invoice-pay-page';
import { getBeneficiaryCardConfig } from './utils/get-beneficiary-card-config';
import { getEvidenceSuggestedTransactions } from './utils/get-evidence-suggested-transactions';
import { getFilteredInvoiceEvidence } from './utils/get-filtered-invoice-evidence';
import { getInReviewInvoices } from './utils/get-in-review-invoices';
import { getInvoiceDueDateLabel } from './utils/get-invoice-due-date-label';
import { getInvoiceMetrics } from './utils/get-invoice-metrics';
import { getLastPaidCardConfig } from './utils/get-last-paid-card-config';
import { useGmailConnection } from './utils/use-gmail-connection';
import { useXeroConnection } from './utils/use-xero-connection';

const POLLING_INTERVAL = 1000;
export const GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY = 'is_gmail_extraction_after_connection_completed';
const GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_VALUE = 'true';
export const INVOICE_SCANNING_CARD_HIDDEN_KEY = 'invoice_scanning_card_hidden';
const INVOICE_SCANNING_CARD_HIDDEN_VALUE = 'true';

const EMAIL_EXTRACTION_STATUS_URL = '/api/email/extraction-status';
export const DOCUMENT_URL = '/api/documents';
const APPROVE_INVOICE_URL = `${EVIDENCE_API_PATH}/approve`;
const CONNECTION_URL = '/api/connection/';

const PAYMENTS_URL = '/payments';

type ClickedMenuItemVariantSource = 'ROW_MENU_ITEM' | 'DRAWER';

export type DisconnectGmailResponse = {
  success: boolean;
  message?: string;
};

// These are the workflow-status statues enum values
// server/src/models/workflow-status.ts
// only these 3 are used server/src/controllers/email-controller/email-controller.ts
type EmailExtractionStatus = 'running' | 'completed' | 'failed';

type EmailExtractionStatusResponse = {
  status: EmailExtractionStatus;
  totalInvoiceCount: number;
};

type MarkAsPaidPayload = {
  isPaid: boolean;
  transactionId?: string;
};

type EmailConnectionStatusResponse = { connections: { excludedSenders: string[] }[]; totalCount: number };

export const InvoicePayPageInnerWrapper = () => {
  const { isReviewMatchLive } = useFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading: isLoadingData, error: dataError, mutations } = useData();
  const [isMenuItemModalOpen, setIsMenuItemModalOpen] = useState(false);
  const [invoiceDrawerConfig, setInvoiceDrawerConfig] = useState<
    { isOpen: false } | { isOpen: true; variant: InvoiceDrawerContentBodyVariant }
  >({
    isOpen: false,
  });
  const [clickedMenuItemVariant, setClickedMenuItemVariant] = useState<
    { menuItem: MenuItemVariant; source: ClickedMenuItemVariantSource } | undefined
  >(undefined);
  const [selectedPaidTransactionId, setSelectedPaidTransactionId] = useState<string | undefined>(undefined);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [isMatchingToTransaction, setIsMatchingToTransaction] = useState(false);
  const [isScanningCardVisible, setIsScanningCardVisible] = useState(
    localStorage.getItem(INVOICE_SCANNING_CARD_HIDDEN_KEY) !== INVOICE_SCANNING_CARD_HIDDEN_VALUE,
  );
  const [tableSortedEvidence, setTableSortedEvidence] = useState<InvoiceEvidenceView[]>([]);
  const {
    xeroConnectionStatus,
    isCheckingXeroConnection,
    isDisconnectingXero,
    mutations: { onDisconnectXero },
  } = useXeroConnection();
  const {
    gmailConnectionStatus: connectionStatus,
    isCheckingGmailConnection: isCheckingConnection,
    isDisconnectingGmail: isDisconnectingEmail,
    mutations: { onDisconnectGmail: onDisconnectEmail },
  } = useGmailConnection();
  const isGmailExtractionAfterConnectionCompleted = localStorage.getItem(
    GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY,
  );
  const { data: emailProgressStatus } = useSWRImmutable<EmailExtractionStatusResponse, Error, string | null>(
    connectionStatus?.connected && isScanningCardVisible
      ? `${EMAIL_EXTRACTION_STATUS_URL}?connectionId=${connectionStatus.connectionId}`
      : null,
    getSwrFetcher,
    {
      refreshInterval: () => {
        if (isGmailExtractionAfterConnectionCompleted !== GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_VALUE) {
          return POLLING_INTERVAL;
        }

        return 0;
      },
      refreshWhenHidden: true,
      onSuccess: async (newDate) => {
        if (newDate.status !== 'completed' && newDate.status !== 'failed') {
          return;
        }

        localStorage.setItem(
          GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY,
          GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_VALUE,
        );

        await mutations.handleIncrementalUpdates();
      },
      onError: (error) => {
        Sentry.captureMessage(error.message, {
          extra: {
            connectionId: connectionStatus?.connectionId,
            message: 'Failed to fetch email extraction status',
          },
        });

        localStorage.removeItem(GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY);
        toast.error('There was a problem checking your email connection status');
      },
    },
  );

  const {
    trigger: triggerDocumentUrl,
    isMutating: isTriggeringDocumentUrl,
    data: documentUrl,
  } = useSWRMutation<{ url: string }, Error, string, { urlSuffix: string }>(DOCUMENT_URL, getSwrFetcherWithDynamicUrl);
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | undefined>(undefined);
  const { trigger: triggerIsPaid, isMutating: isTriggeringIsPaid } = useSWRMutation<
    InvoiceEvidenceView,
    Error,
    string,
    MarkAsPaidPayload & { urlSuffix: string }
  >(EVIDENCE_API_PATH, postSwrFetcherWithDynamicUrl);
  const { excludeSender, isExcludingSender } = useExcludeSender();
  const { trigger: triggerMarkNotInvoice, isMutating: isMarkingNotInvoice } = useSWRMutation<
    { message: string },
    Error,
    string,
    { urlSuffix: string }
  >(EVIDENCE_API_PATH, deleteSwrFetcherWithDynamicUrl);
  const {
    data: emailConnectionStatus,
    isLoading: isLoadingEmailConnectionStatus,
    error: emailConnectionStatusError,
    mutate: mutateEmailConnectionStatus,
  } = useSWR<EmailConnectionStatusResponse, Error, string>(CONNECTION_URL);
  const { trigger: triggerApproveInvoices } = useSWRMutation<
    { message: string },
    Error,
    string,
    { invoiceIds: string[] }
  >(APPROVE_INVOICE_URL, postSwrFetcher);
  const [approvingEvidenceId, setApprovingEvidenceId] = useState<string[] | undefined>(undefined);

  if (isLoadingData || isLoadingEmailConnectionStatus) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data || !emailConnectionStatus) {
    return <ErrorSection error={dataError || emailConnectionStatusError} />;
  }

  const { transactions, evidence, baseCurrency, documents } = data;

  const onProceedToPayment = async () => {
    const selectedEvidence = evidence.find((e) => e.id === selectedEvidenceId);

    if (!selectedEvidence) {
      Sentry.captureMessage('Selected evidence not found in InvoicePayPageInnerWrapper', {
        extra: {
          selectedEvidenceId,
          evidenceIds: evidence.map((e) => e.id),
          message: 'Selected evidence not found in InvoicePayPageInnerWrapper',
        },
      });

      return;
    }

    if (!selectedEvidence.document) {
      Sentry.captureMessage('Selected evidence has no document linked in InvoicePayPageInnerWrapper', {
        extra: {
          selectedEvidenceId,
          evidenceIds: evidence.map((e) => e.id),
          document: selectedEvidence.document,
          documentId: selectedEvidence.documentId,
          documentIds: documents.map((d) => d.id),
          message: 'Selected evidence has no document linked in InvoicePayPageInnerWrapper',
        },
      });

      return;
    }

    if (!selectedEvidence.documentId) {
      Sentry.captureMessage('Selected evidence has no document id', {
        extra: {
          selectedEvidenceId,
          evidenceIds: evidence.map((e) => e.id),
          message: 'Selected evidence has no document id',
        },
      });
    }

    try {
      const result = await triggerDocumentUrl({ urlSuffix: `/${selectedEvidence.documentId}/signed-url` });

      navigate(`${PAYMENTS_URL}?action=BILL_PAY`, {
        state: {
          isFromToPayRowReviewInvoicePageConfig: {
            isFromToPayRowReviewInvoicePage: true,
            invoiceId: selectedEvidenceId,
            invoiceReference: selectedEvidence.invoiceDetails?.invoiceReference,
            fileName: selectedEvidence.document.fileName,
            documentUrl: result,
          },
        },
      });

      return;
    } catch (error) {
      Sentry.captureException(error);

      setSelectedEvidenceId(undefined);
      toast.error('Failed to fetch invoice details');
    }
  };

  const resetMenuItemState = () => {
    setIsMenuItemModalOpen(false);
    setClickedMenuItemVariant(undefined);

    if (clickedMenuItemVariant?.source === 'ROW_MENU_ITEM') {
      setSelectedEvidenceId(undefined);
    }

    setSelectedPaidTransactionId(undefined);
  };

  const clearModalSearchParams = () => {
    const newParams = new URLSearchParams(searchParams);
    // Clear modal-related params but preserve tab selection
    newParams.delete(PAGE_PARAM_NAME);
    newParams.delete(GLOBAL_FILTER_PARAM_NAME);
    setSearchParams(newParams);
  };

  const onHideCard = () => {
    localStorage.setItem(INVOICE_SCANNING_CARD_HIDDEN_KEY, INVOICE_SCANNING_CARD_HIDDEN_VALUE);
    setIsScanningCardVisible(false);
  };

  const emailScanningProgressConfig = getEmailScanningProgressStatus({ emailProgressStatus, onHideCard });
  const invoiceEvidences = getFilteredInvoiceEvidence(evidence as InvoiceEvidenceView[], isReviewMatchLive);
  const invoicesMetrics = getInvoiceMetrics({ evidences: invoiceEvidences, baseCurrency });

  const unpaidInvoices = invoiceEvidences.filter((e) => e.isPaid === false);
  const inReviewInvoices = getInReviewInvoices(invoiceEvidences);
  const completedInvoices = isReviewMatchLive
    ? invoiceEvidences.filter((e) => e.status === EvidenceStatus.COMPLETED)
    : invoiceEvidences.filter((e) => e.isPaid === true);

  const selectedEvidence = invoiceEvidences.find((e) => e.id === selectedEvidenceId);
  const selectedEvidenceSuggestedTransactions = getEvidenceSuggestedTransactions(selectedEvidence);
  const suggestedTransactions = transactions.filter((t) => selectedEvidenceSuggestedTransactions?.includes(t.id));

  const onApproveInvoice = async (invoiceIds: string[]) => {
    try {
      setApprovingEvidenceId(invoiceIds);

      await triggerApproveInvoices({ invoiceIds });

      if (invoiceIds.length === 1) {
        toast.success('Invoice approved');
      } else {
        toast.success('Invoices approved');
      }

      await mutations.handleIncrementalUpdates();
    } catch (error) {
      Sentry.captureException(error);

      if (invoiceIds.length === 1) {
        toast.error('Failed to approve invoice. Please try again.');
      } else {
        toast.error('Failed to approve invoices. Please try again.');
      }

      toast.error('Failed to approve invoice. Please try again.');
    } finally {
      setApprovingEvidenceId(undefined);
    }
  };

  const onToggleSelectedTransaction = (transactionId: string) => {
    if (selectedPaidTransactionId) {
      setSelectedPaidTransactionId(undefined);

      return;
    }

    setSelectedPaidTransactionId(transactionId);
  };

  return (
    <>
      <InvoicePayPage
        evidenceDataConfig={{
          unpaidInvoices,
          inReviewInvoices,
          completedInvoices,
        }}
        connectionsAndSettingsConfig={{
          gmailConfig: {
            type: OAuthProvider.GMAIL,
            connectionStatus,
            isChecking: isCheckingConnection,
            isDisconnecting: isDisconnectingEmail,
            onDisconnect: onDisconnectEmail,
            onConnect: () => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('email-modal-open', 'empty-state');
              setSearchParams(newParams);
            },
          },
          xeroConfig: {
            type: OAuthProvider.XERO,
            connectionStatus: xeroConnectionStatus,
            isChecking: isCheckingXeroConnection,
            isDisconnecting: isDisconnectingXero,
            onDisconnect: onDisconnectXero,
          },
          emailConnectionStatusConfig: {
            connectionStatus: emailConnectionStatus,
            mutateConnectionStatus: async () => {
              await mutateEmailConnectionStatus();
            },
          },
          onDeleteEvidence: async (evidenceIds) => {
            await mutations.handleDeleteUpdates({
              entityType: 'evidence',
              entityIds: evidenceIds,
            });
          },
        }}
        invoicesMetrics={invoicesMetrics}
        emailScanningProgressConfig={emailScanningProgressConfig}
        isFetchingDocumentUrl={isTriggeringDocumentUrl}
        selectedEvidenceId={selectedEvidenceId}
        onReviewInvoice={async (invoiceId, variant) => {
          const selectedEvidence = evidence.find((e) => e.id === invoiceId);

          if (!selectedEvidence || !selectedEvidence.document) {
            Sentry.captureMessage('Selected evidence not found on onReviewInvoice in InvoicePayPageInnerWrapper', {
              extra: {
                selectedEvidenceId,
                evidenceIds: evidence.map((e) => e.id),
                message: 'Selected evidence not found on onReviewInvoice in InvoicePayPageInnerWrapper',
              },
            });

            return;
          }

          try {
            await triggerDocumentUrl({ urlSuffix: `/${selectedEvidence.documentId}/signed-url` });

            setInvoiceDrawerConfig({ isOpen: true, variant });

            setSelectedEvidenceId(invoiceId);
          } catch (error) {
            Sentry.captureException(error);

            toast.error('Failed to load invoice details');
          }
        }}
        onSelectMenuItem={(payload) => {
          setIsMenuItemModalOpen(true);
          setSelectedEvidenceId(payload.entityId);
          setClickedMenuItemVariant({ menuItem: payload.menuItemVariant as MenuItemVariant, source: 'ROW_MENU_ITEM' });
        }}
        onDataSorted={setTableSortedEvidence}
        onApproveInvoice={onApproveInvoice}
        approvingEvidenceId={approvingEvidenceId}
      />

      {clickedMenuItemVariant &&
        ['MARK_PAID', 'CHANGE_TRANSACTION_MATCH', 'MATCH_TO_DIFFERENT_TRANSACTION'].includes(
          clickedMenuItemVariant.menuItem,
        ) &&
        selectedEvidence && (
          <MarkAsPaidModal
            isOpen={isMenuItemModalOpen}
            invoiceInfo={getInvoiceInfo(selectedEvidence)}
            onClose={() => {
              resetMenuItemState();
              clearModalSearchParams();
            }}
            transactions={suggestedTransactions.length > 0 ? suggestedTransactions : transactions}
            selectedPaidTransactionId={selectedPaidTransactionId}
            canMarkAsPaidWithoutMatching={clickedMenuItemVariant?.menuItem === 'MARK_PAID'}
            isMatching={isMatchingToTransaction}
            isMarkingAsPaid={isMarkingAsPaid}
            onToggleTransaction={onToggleSelectedTransaction}
            onMatchToTransaction={async () => {
              if (!selectedPaidTransactionId) {
                return;
              }

              setIsMatchingToTransaction(true);

              try {
                await triggerIsPaid({
                  urlSuffix: `/${selectedEvidence.id}/is-paid`,
                  isPaid: true,
                  transactionId: selectedPaidTransactionId,
                });

                await mutations.handleIncrementalUpdates();

                resetMenuItemState();
                clearModalSearchParams();

                if (clickedMenuItemVariant?.menuItem === 'MARK_PAID') {
                  toast.success('Invoice marked as paid', {
                    description: `We've removed it from your payment queue and updated your records.`,
                  });

                  return;
                }

                toast.success('Transaction match changed', {
                  description: `We've updated your records.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as paid');
              } finally {
                setIsMatchingToTransaction(false);
              }
            }}
            onMarkAsPaidWithoutMatching={async () => {
              setIsMarkingAsPaid(true);

              try {
                await triggerIsPaid({
                  urlSuffix: `/${selectedEvidence.id}/is-paid`,
                  isPaid: true,
                });

                await mutations.handleIncrementalUpdates();

                resetMenuItemState();
                clearModalSearchParams();

                if (clickedMenuItemVariant?.menuItem === 'MARK_PAID') {
                  toast.success('Invoice marked as paid', {
                    description: `We've removed it from your payment queue and updated your records.`,
                  });

                  return;
                }

                toast.success('Transaction match changed', {
                  description: `We've updated your records.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as paid');
              } finally {
                setIsMarkingAsPaid(false);
              }
            }}
          />
        )}

      {clickedMenuItemVariant?.menuItem === 'EXCLUDE_SENDER' && selectedEvidence && (
        <ExcludeSenderModal
          isOpen={isMenuItemModalOpen}
          onClose={resetMenuItemState}
          invoiceInfo={getInvoiceInfo(selectedEvidence)}
          isLoading={isExcludingSender}
          onExcludeSender={async () => {
            if (!selectedEvidence) {
              Sentry.captureMessage('No selected evidence found when trying to exclude sender', {
                extra: {
                  evidenceIds: evidence.map((e) => e.id),
                  message: 'No selected evidence found when trying to exclude sender',
                },
              });

              return;
            }

            if (!connectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to exclude sender', {
                extra: {
                  selectedEvidenceId: selectedEvidence.id,
                  connectionStatus,
                  message: 'No connection id found when trying to exclude sender',
                },
              });

              return;
            }

            if (!selectedEvidence.emailInformation?.fromEmail) {
              Sentry.captureMessage('No from email found when trying to exclude sender', {
                extra: {
                  selectedEvidenceId: selectedEvidence.id,
                  evidenceEmailInformation: selectedEvidence.emailInformation,
                  message: 'No from email found when trying to exclude sender',
                },
              });

              return;
            }

            const excludedSenders: string[] = [];
            excludedSenders.push(selectedEvidence.emailInformation.fromEmail);

            try {
              const { deletedEvidenceIds } = await excludeSender({
                urlSuffix: `/${connectionStatus.connectionId}/excluded-senders`,
                excludedSenders,
              });

              await mutateEmailConnectionStatus();
              await mutations.handleDeleteUpdates({
                entityType: 'evidence',
                entityIds: deletedEvidenceIds,
              });

              resetMenuItemState();

              toast.success('Sender excluded', {
                description: `We won't pull in any more invoices from this email address.`,
              });
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to exclude sender');
            }
          }}
        />
      )}

      {clickedMenuItemVariant &&
        ['MARK_NOT_INVOICE', 'DELETE_INVOICE'].includes(clickedMenuItemVariant.menuItem) &&
        selectedEvidence && (
          <MarkNotInvoiceModal
            isOpen={isMenuItemModalOpen}
            invoiceInfo={getInvoiceInfo(selectedEvidence)}
            isLoading={isMarkingNotInvoice}
            onClose={resetMenuItemState}
            onMarkNotInvoice={async () => {
              if (!selectedEvidence) {
                Sentry.captureMessage('No selected evidence found when trying to mark not invoice', {
                  extra: {
                    evidenceIds: evidence.map((e) => e.id),
                    message: 'No selected evidence found when trying to mark not invoice',
                  },
                });

                return;
              }

              try {
                await triggerMarkNotInvoice({ urlSuffix: `/${selectedEvidence.id}` });

                await mutations.handleDeleteUpdates({
                  entityType: 'evidence',
                  entityIds: [selectedEvidence.id],
                });

                resetMenuItemState();

                toast.success('Marked as not an invoice', {
                  description: `We've removed it from your list to keep things accurate.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as not invoice');
              }
            }}
          />
        )}

      {clickedMenuItemVariant &&
        ['MARK_NOT_PAID', 'I_HAVEN_T_PAID_THIS_INVOICE_YET'].includes(clickedMenuItemVariant.menuItem) &&
        selectedEvidence && (
          <MarkNotPaidModal
            isOpen={isMenuItemModalOpen}
            invoiceInfo={getInvoiceInfo(selectedEvidence)}
            isLoading={isTriggeringIsPaid}
            onClose={resetMenuItemState}
            onMarkNotPaid={async () => {
              try {
                await triggerIsPaid({
                  urlSuffix: `/${selectedEvidence.id}/is-paid`,
                  isPaid: false,
                });

                resetMenuItemState();

                await mutations.handleIncrementalUpdates();

                toast.success('Marked as unpaid', {
                  description: `We've moved it back to your invoice queue so you can review and pay it.`,
                });
              } catch (error) {
                Sentry.captureException(error);

                toast.error('Failed to mark this invoice as unpaid');
              }
            }}
          />
        )}

      {selectedEvidence && documentUrl && (
        <InvoiceDrawer
          isOpen={invoiceDrawerConfig.isOpen}
          onClose={() => setInvoiceDrawerConfig({ isOpen: false })}
          invoiceDrawerContentConfig={{
            invoiceVendor:
              selectedEvidence.suggestedBeneficiary?.name ??
              selectedEvidence.vendorInformation?.vendorCompanyName ??
              'Unknown',
            pdfConfig: {
              url: documentUrl.url,
            },
            navigationConfig: {
              isVisible: true,
              isFirstInvoice: getEvidenceIndex(tableSortedEvidence, selectedEvidence) === 0,
              isLastInvoice: getEvidenceIndex(tableSortedEvidence, selectedEvidence) === tableSortedEvidence.length - 1,
              onPrevious: async () => {
                const currentIndex = getEvidenceIndex(tableSortedEvidence, selectedEvidence);

                if (currentIndex === 0) {
                  return;
                }

                const previousEvidence = tableSortedEvidence[currentIndex - 1];

                await triggerDocumentUrl({ urlSuffix: `/${previousEvidence.documentId}/signed-url` });

                setSelectedEvidenceId(previousEvidence.id);
              },
              onNext: async () => {
                const currentIndex = getEvidenceIndex(tableSortedEvidence, selectedEvidence);

                if (currentIndex === tableSortedEvidence.length - 1) {
                  return;
                }

                const nextEvidence = tableSortedEvidence[currentIndex + 1];

                await triggerDocumentUrl({ urlSuffix: `/${nextEvidence.documentId}/signed-url` });

                setSelectedEvidenceId(nextEvidence.id);
              },
            },
            contentBodyConfig:
              invoiceDrawerConfig.isOpen && invoiceDrawerConfig.variant === 'TO_PAY'
                ? {
                    variant: 'TO_PAY',
                    isDismissingInvoice: isMarkingNotInvoice || isExcludingSender,
                    lastPaidCardConfig: getLastPaidCardConfig({
                      suggestedBeneficiaryId: selectedEvidence.suggestedBeneficiary?.id,
                      transactions,
                    }),
                    beneficiaryCardConfig: getBeneficiaryCardConfig(selectedEvidence),
                    summaryConfig:
                      selectedEvidence.summary || selectedEvidence.emailInformation?.fromEmail
                        ? {
                            isVisible: true,
                            summary: selectedEvidence.summary,
                            fromEmail: selectedEvidence.emailInformation?.fromEmail,
                            onMarkAsPaid: () => {
                              setIsMenuItemModalOpen(true);
                              setClickedMenuItemVariant({ menuItem: 'MARK_PAID', source: 'DRAWER' });
                            },
                          }
                        : {
                            isVisible: false,
                            onMarkAsPaid: () => {
                              setIsMenuItemModalOpen(true);
                              setClickedMenuItemVariant({ menuItem: 'MARK_PAID', source: 'DRAWER' });
                            },
                          },
                    onProceedToPayment,
                    onExcludeSender: () => {
                      setIsMenuItemModalOpen(true);
                      setClickedMenuItemVariant({ menuItem: 'EXCLUDE_SENDER', source: 'DRAWER' });
                    },
                  }
                : invoiceDrawerConfig.isOpen && invoiceDrawerConfig.variant === 'PAID_NO_TRANSACTION'
                  ? {
                      variant: 'PAID_NO_TRANSACTION',
                      beneficiaryCardConfig: getBeneficiaryCardConfig(selectedEvidence),
                      summaryConfig:
                        selectedEvidence.summary || selectedEvidence.emailInformation?.fromEmail
                          ? {
                              isVisible: true,
                              summary: selectedEvidence.summary,
                              fromEmail: selectedEvidence.emailInformation?.fromEmail,
                            }
                          : {
                              isVisible: false,
                            },
                    }
                  : {
                      variant: 'REVIEW_MATCHES',
                      confidenceScore: selectedEvidence.isPaidConfidenceScore ?? 0,
                      hasMatchedTransaction: !!selectedEvidence.transactionId,
                      cardConfig: {
                        name:
                          selectedEvidence.suggestedBeneficiary?.name ??
                          selectedEvidence.vendorInformation?.vendorCompanyName ??
                          'Unknown',
                        date: selectedEvidence.transaction?.fspDate
                          ? format(new UTCDate(selectedEvidence.transaction.fspDate), 'dd MMM')
                          : 'Unknown',
                        reference:
                          selectedEvidence.transaction?.type === TransactionType.TRANSFER ||
                          selectedEvidence.transaction?.type === TransactionType.DIRECT_DEBIT
                            ? selectedEvidence.transaction?.reference
                            : null,
                        formattedAmount:
                          !selectedEvidence.transaction?.amount ||
                          !selectedEvidence.transaction?.currency ||
                          !isSupportedCurrency(selectedEvidence.transaction?.currency)
                            ? '-'
                            : getAmountWithCurrency({
                                amount: selectedEvidence.transaction?.amount,
                                currency: selectedEvidence.transaction?.currency,
                                isAccountBalance: true,
                              }),
                        onChangeMatchedTransaction: () => {
                          setIsMenuItemModalOpen(true);
                          setClickedMenuItemVariant({
                            menuItem: 'MATCH_TO_DIFFERENT_TRANSACTION' as const,
                            source: 'DRAWER',
                          });
                        },
                        onRejectInvoice: () => {
                          setIsMenuItemModalOpen(true);
                          setClickedMenuItemVariant({ menuItem: 'MARK_NOT_INVOICE' as const, source: 'DRAWER' });
                        },
                      },
                      isApproveMatchLoading: !!approvingEvidenceId,
                      onApproveMatch: () => onApproveInvoice([selectedEvidence.id]),
                    },
            onMarkNotInvoice: () => {
              setIsMenuItemModalOpen(true);
              setClickedMenuItemVariant({ menuItem: 'MARK_NOT_INVOICE', source: 'DRAWER' });
            },
          }}
        />
      )}
    </>
  );
};

type GetEmailScanningProgressStatusProps = {
  emailProgressStatus?: EmailExtractionStatusResponse;
  onHideCard: () => void;
};

const getEmailScanningProgressStatus = ({
  emailProgressStatus,
  onHideCard,
}: GetEmailScanningProgressStatusProps): ComponentProps<typeof InvoicePayPage>['emailScanningProgressConfig'] => {
  if (
    !emailProgressStatus ||
    localStorage.getItem(INVOICE_SCANNING_CARD_HIDDEN_KEY) === INVOICE_SCANNING_CARD_HIDDEN_VALUE
  ) {
    return { isVisible: false };
  }

  const { status, totalInvoiceCount } = emailProgressStatus;

  switch (status) {
    case 'failed':
      return { isVisible: true, status: 'FAILED', invoiceCount: totalInvoiceCount, onHideCard };
    case 'running':
      return {
        isVisible: true,
        status: 'EXTRACTING',
        invoiceCount: totalInvoiceCount,
        onHideCard,
      };
    case 'completed':
      return {
        isVisible: true,
        status: 'COMPLETE',
        invoiceCount: totalInvoiceCount,
        onHideCard,
      };
    default:
      return exhaustiveCheck(status);
  }
};

const getInvoiceInfo = (evidence: InvoiceEvidenceView) => {
  const dateString = getInvoiceDueDateLabel({
    dueDate: evidence.billingDates?.dueDate,
    invoiceDate: evidence.billingDates?.invoiceDate,
  });

  return {
    // Note: invoiceDate can be either due date or invoice date (prioritizes due date)
    invoiceDate: dateString && dateString !== 'Unknown' ? new UTCDate(dateString) : undefined,
    vendorName: evidence.suggestedBeneficiary?.name ?? evidence.vendorInformation?.vendorCompanyName ?? 'Unknown',
    invoiceReference: evidence.invoiceDetails?.invoiceReference ?? 'Unknown',
    amount: evidence.invoiceDetails?.totalAmount,
    currency: evidence.invoiceDetails?.currency,
  };
};

const getEvidenceIndex = (evidenceList: InvoiceEvidenceView[], selectedEvidence: InvoiceEvidenceView) => {
  return evidenceList.map((e) => e.id).indexOf(selectedEvidence.id);
};
